import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import styled from 'styled-components';
import {
  listCategories,
  createCategory,
  updateCategory,
  toggleCategory,
  deleteCategory,
  normalizeCategory,
} from '../../services/advisoryCategoryService';

const Container = styled.div`
  background: rgba(255, 255, 255, 0.08);
  backdrop-filter: blur(10px);
  border-radius: 12px;
  padding: 1.5rem 2rem;
  margin-bottom: 2rem;
  border: 1px solid rgba(255, 255, 255, 0.12);
`;

const Toolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 1rem;
  margin-bottom: 1.5rem;
  justify-content: space-between;
`;

const ToolbarLeft = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  flex: 1;
  min-width: 0;
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  min-width: 160px;
  flex: 1;
  max-width: 280px;
`;

const ToolbarLabel = styled.label`
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: rgba(255, 255, 255, 0.65);
`;

const SearchInput = styled.input`
  padding: 0.65rem 0.85rem;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  background: rgba(0, 0, 0, 0.2);
  color: #fff;
  font-size: 0.9rem;
  width: 100%;
  box-sizing: border-box;

  &::placeholder {
    color: rgba(255, 255, 255, 0.45);
  }
  &:focus {
    outline: none;
    border-color: rgba(102, 126, 234, 0.8);
  }
`;

const FilterSelect = styled.select`
  padding: 0.65rem 0.85rem;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  background: rgba(0, 0, 0, 0.25);
  color: #fff;
  font-size: 0.9rem;
  min-width: 140px;

  option {
    color: #1a202c;
  }
  &:focus {
    outline: none;
    border-color: rgba(102, 126, 234, 0.8);
  }
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1rem;
  flex-wrap: wrap;
`;

const PageTitle = styled.h2`
  color: #fff;
  font-size: 1.35rem;
  font-weight: 600;
  margin: 0;
  letter-spacing: -0.02em;
`;

const Button = styled.button`
  padding: 10px 20px;
  background: rgba(102, 126, 234, 0.95);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;

  &:hover:not(:disabled) {
    background: #5a67d8;
  }
  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

const SecondaryButton = styled.button`
  padding: 8px 14px;
  background: transparent;
  color: rgba(255, 255, 255, 0.95);
  border: 1px solid rgba(255, 255, 255, 0.35);
  border-radius: 6px;
  font-size: 0.75rem;
  font-weight: 600;
  cursor: pointer;

  &:hover:not(:disabled) {
    background: rgba(255, 255, 255, 0.08);
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const DangerButton = styled.button`
  padding: 8px 14px;
  background: transparent;
  color: #feb2b2;
  border: 1px solid rgba(254, 178, 178, 0.5);
  border-radius: 6px;
  font-size: 0.75rem;
  font-weight: 600;
  cursor: pointer;

  &:hover:not(:disabled) {
    background: rgba(229, 62, 62, 0.25);
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const TableWrap = styled.div`
  width: 100%;
  overflow-x: auto;
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, 0.1);
`;

const Table = styled.table`
  width: 100%;
  min-width: 800px;
  border-collapse: collapse;
  background: rgba(0, 0, 0, 0.15);
`;

const Th = styled.th`
  text-align: left;
  padding: 12px 14px;
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: rgba(255, 255, 255, 0.55);
  border-bottom: 1px solid rgba(255, 255, 255, 0.12);
  background: rgba(0, 0, 0, 0.2);
`;

const Td = styled.td`
  padding: 12px 14px;
  font-size: 0.875rem;
  color: rgba(255, 255, 255, 0.92);
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  vertical-align: middle;
`;

const Tr = styled.tr`
  &:hover td {
    background: rgba(255, 255, 255, 0.04);
  }
`;

const StatusPill = styled.span`
  display: inline-block;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  background: ${(p) =>
    p.$deleted
      ? 'rgba(113, 128, 150, 0.35)'
      : p.$active
        ? 'rgba(72, 187, 120, 0.25)'
        : 'rgba(237, 137, 54, 0.25)'};
  color: ${(p) =>
    p.$deleted ? '#e2e8f0' : p.$active ? '#9ae6b4' : '#fbd38d'};
  border: 1px solid
    ${(p) =>
      p.$deleted
        ? 'rgba(160, 174, 192, 0.4)'
        : p.$active
          ? 'rgba(72, 187, 120, 0.45)'
          : 'rgba(237, 137, 54, 0.45)'};
`;

const ActionsCell = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

const Message = styled.div`
  padding: 12px 16px;
  border-radius: 8px;
  margin-bottom: 1rem;
  font-size: 0.875rem;
  font-weight: 500;

  ${({ $success }) =>
    $success &&
    `
    background: rgba(72, 187, 120, 0.2);
    color: #c6f6d5;
    border: 1px solid rgba(72, 187, 120, 0.35);
  `}
  ${({ $error }) =>
    $error &&
    `
    background: rgba(245, 101, 101, 0.15);
    color: #fed7d7;
    border: 1px solid rgba(245, 101, 101, 0.35);
  `}
`;

const Modal = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.75);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
`;

const ModalCard = styled.div`
  background: #f7fafc;
  border-radius: 12px;
  max-width: 600px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  padding: 1.75rem;
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.35);
`;

const ModalTitle = styled.h3`
  margin: 0 0 1.25rem;
  font-size: 1.2rem;
  color: #1a202c;
  font-weight: 600;
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1rem;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
`;

const Label = styled.label`
  font-size: 0.8rem;
  font-weight: 600;
  color: #4a5568;
`;

const Input = styled.input`
  padding: 10px 12px;
  border: 1px solid #cbd5e0;
  border-radius: 8px;
  font-size: 0.875rem;
  box-sizing: border-box;
  width: 100%;

  &:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 2px rgba(102, 126, 234, 0.15);
  }
`;

const TextArea = styled.textarea`
  padding: 10px 12px;
  border: 1px solid #cbd5e0;
  border-radius: 8px;
  font-size: 0.875rem;
  min-height: 80px;
  resize: vertical;
  font-family: inherit;
  box-sizing: border-box;
  width: 100%;

  &:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 2px rgba(102, 126, 234, 0.15);
  }
`;

const FieldError = styled.span`
  font-size: 0.75rem;
  color: #c53030;
`;

const ButtonRow = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  margin-top: 1.5rem;
  flex-wrap: wrap;
`;

const GhostButton = styled.button`
  padding: 10px 18px;
  border-radius: 8px;
  border: 1px solid #cbd5e0;
  background: #fff;
  color: #2d3748;
  font-weight: 600;
  font-size: 0.875rem;
  cursor: pointer;

  &:hover {
    background: #edf2f7;
  }
`;

const PrimaryModalButton = styled.button`
  padding: 10px 18px;
  border-radius: 8px;
  border: none;
  background: #5a67d8;
  color: #fff;
  font-weight: 600;
  font-size: 0.875rem;
  cursor: pointer;

  &:hover:not(:disabled) {
    background: #4c51bf;
  }
  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 2.5rem 1rem;
  color: rgba(255, 255, 255, 0.55);
  font-size: 0.9rem;
`;

const DEBOUNCE_MS = 320;

function generateSlug(name) {
  return String(name || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-+|-+$/g, '');
}

function validateForm(values) {
  const errors = {};
  const name = String(values.name || '').trim();
  if (name.length < 2) errors.name = 'Name is required (min 2 characters).';

  const slug = String(values.slug || '').trim();
  if (!slug) errors.slug = 'Slug is required.';
  if (!/^[a-z0-9-]+$/.test(slug)) {
    errors.slug = 'Slug must contain only lowercase letters, numbers, and hyphens.';
  }

  const rawOrder = values.display_order;
  if (rawOrder === '' || rawOrder === null || rawOrder === undefined) {
    errors.display_order = 'Display order is required.';
  } else {
    const order = Number(rawOrder);
    if (!Number.isFinite(order) || !Number.isInteger(order) || order < 0) {
      errors.display_order = 'Display order must be an integer ≥ 0.';
    }
  }

  return errors;
}

function buildPayload(values) {
  return {
    name: String(values.name || '').trim(),
    slug: String(values.slug || '').trim(),
    description: String(values.description || '').trim(),
    display_order: Math.max(0, parseInt(values.display_order, 10) || 0),
  };
}

function buildEditSnapshot(row) {
  return {
    name: String(row.name ?? '').trim(),
    slug: String(row.slug ?? '').trim(),
    description: String(row.description ?? '').trim(),
    display_order: Math.max(0, parseInt(row.display_order, 10) || 0),
  };
}

function buildPartialUpdate(current, baseline) {
  if (!baseline) return buildPayload(current);
  const patch = {};
  const s = (v) => String(v ?? '').trim();

  if (s(current.name) !== s(baseline.name)) patch.name = s(current.name);
  if (s(current.slug) !== s(baseline.slug)) patch.slug = s(current.slug);
  if (s(current.description) !== s(baseline.description)) patch.description = s(current.description);

  const cOrder = Math.max(0, parseInt(current.display_order, 10) || 0);
  const bOrder = Math.max(0, parseInt(baseline.display_order, 10) || 0);
  if (cOrder !== bOrder) patch.display_order = cOrder;

  return patch;
}

const defaultForm = () => ({
  name: '',
  slug: '',
  description: '',
  display_order: 0,
});

function AdvisoryCategories({ showMessage }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [listError, setListError] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [formModal, setFormModal] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [formValues, setFormValues] = useState(defaultForm);
  const [fieldErrors, setFieldErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [banner, setBanner] = useState({ text: '', type: '' });
  const searchTimer = useRef(null);
  const editBaselineRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    setListError('');
    try {
      const data = await listCategories();
      setRows(data);
    } catch (e) {
      setListError(e.message || 'Could not load categories.');
      setRows([]);
      if (e.status === 401 && showMessage) {
        showMessage('Session expired or unauthorized. Please sign in again.', 'error');
      }
    } finally {
      setLoading(false);
    }
  }, [showMessage]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      setDebouncedSearch(searchInput.trim().toLowerCase());
    }, DEBOUNCE_MS);
    return () => {
      if (searchTimer.current) clearTimeout(searchTimer.current);
    };
  }, [searchInput]);

  const filteredRows = useMemo(() => {
    let list = rows;

    if (statusFilter === 'active') {
      list = list.filter((r) => r.is_active && !r.deleted_at);
    } else if (statusFilter === 'inactive') {
      list = list.filter((r) => !r.is_active && !r.deleted_at);
    }

    if (!debouncedSearch) return list;

    return list.filter((r) => {
      const hay = [r.name, r.slug, r.description].join(' ').toLowerCase();
      return hay.includes(debouncedSearch);
    });
  }, [rows, statusFilter, debouncedSearch]);

  const openCreate = () => {
    editBaselineRef.current = null;
    setFormValues(defaultForm());
    setFieldErrors({});
    setFormModal({ mode: 'create' });
  };

  const openEdit = (row) => {
    editBaselineRef.current = buildEditSnapshot(row);
    setFormValues({
      name: row.name,
      slug: row.slug,
      description: row.description,
      display_order: row.display_order,
    });
    setFieldErrors({});
    setFormModal({ mode: 'edit', id: row.id });
  };

  const closeForm = () => {
    editBaselineRef.current = null;
    setFormModal(null);
    setFieldErrors({});
    setFormValues(defaultForm());
  };

  const setBannerMsg = (text, type = 'success') => {
    setBanner({ text, type });
    setTimeout(() => setBanner({ text: '', type: '' }), 4000);
  };

  const onSubmitForm = async (e) => {
    e.preventDefault();
    setFieldErrors({});

    const initialErrors = validateForm(formValues);
    if (Object.keys(initialErrors).length) {
      setFieldErrors(initialErrors);
      return;
    }

    setSubmitting(true);
    try {
      if (formModal.mode === 'edit') {
        const baseline = editBaselineRef.current;
        const patch = buildPartialUpdate(formValues, baseline);
        if (Object.keys(patch).length === 0) {
          setFieldErrors({ _form: 'No changes to save.' });
          return;
        }
        await updateCategory(formModal.id, patch);
        setBannerMsg('Category updated.', 'success');
        if (showMessage) showMessage('Category updated successfully.', 'success');
      } else {
        const payload = buildPayload(formValues);
        await createCategory(payload);
        setBannerMsg('Category created.', 'success');
        if (showMessage) showMessage('Category created successfully.', 'success');
      }
      closeForm();
      await load();
    } catch (err) {
      const msg = err.message || 'Request failed.';
      setFieldErrors({ _form: msg });
      if (showMessage) showMessage(msg, 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const onToggle = async (row) => {
    const prev = rows.slice();
    const nextActive = !row.is_active;
    setRows((p) =>
      p.map((r) => (r.id === row.id ? { ...r, is_active: nextActive } : r))
    );
    try {
      const raw = await toggleCategory(row.id, nextActive);
      const updated = normalizeCategory(raw);
      if (updated) {
        setRows((p) => p.map((r) => (r.id === row.id ? { ...r, ...updated } : r)));
      } else {
        await load();
      }
      if (showMessage) showMessage('Status updated.', 'success');
    } catch (err) {
      setRows(prev);
      const msg = err.message || 'Toggle failed.';
      setBannerMsg(msg, 'error');
      if (showMessage) showMessage(msg, 'error');
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setSubmitting(true);
    try {
      await deleteCategory(deleteTarget.id);
      setDeleteTarget(null);
      if (showMessage) showMessage('Category removed.', 'success');
      await load();
    } catch (err) {
      const msg = err.message || 'Delete failed.';
      if (showMessage) showMessage(msg, 'error');
      setBannerMsg(msg, 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleNameChange = (value) => {
    setFormValues((prev) => ({
      ...prev,
      name: value,
      slug: generateSlug(value),
    }));
  };

  const formMode = formModal?.mode;

  return (
    <Container>
      <TitleRow>
        <PageTitle>Advisory Categories</PageTitle>
        <Button type="button" onClick={openCreate}>
          Add Category
        </Button>
      </TitleRow>

      {banner.text && (
        <Message $success={banner.type === 'success'} $error={banner.type === 'error'}>
          {banner.text}
        </Message>
      )}

      <Toolbar>
        <ToolbarLeft>
          <Field>
            <ToolbarLabel htmlFor="cat-search">Search</ToolbarLabel>
            <SearchInput
              id="cat-search"
              placeholder="Name, slug, description"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              autoComplete="off"
            />
          </Field>
          <Field>
            <ToolbarLabel htmlFor="cat-filter">Status</ToolbarLabel>
            <FilterSelect
              id="cat-filter"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="all">All</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </FilterSelect>
          </Field>
        </ToolbarLeft>
      </Toolbar>

      {listError && (
        <Message $error>
          {listError}
        </Message>
      )}

      <TableWrap>
        <Table>
          <thead>
            <tr>
              <Th>Name</Th>
              <Th>Slug</Th>
              <Th>Description</Th>
              <Th>Order</Th>
              <Th>Status</Th>
              <Th>Actions</Th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <Tr>
                <Td colSpan={6}>
                  <EmptyState>Loading…</EmptyState>
                </Td>
              </Tr>
            ) : filteredRows.length === 0 ? (
              <Tr>
                <Td colSpan={6}>
                  <EmptyState>No categories match the current filters.</EmptyState>
                </Td>
              </Tr>
            ) : (
              filteredRows.map((row) => (
                <Tr key={row.id}>
                  <Td>{row.name}</Td>
                  <Td>{row.slug}</Td>
                  <Td>{row.description || '-'}</Td>
                  <Td>{row.display_order}</Td>
                  <Td>
                    <StatusPill $active={row.is_active} $deleted={!!row.deleted_at}>
                      {row.deleted_at ? 'Deleted' : row.is_active ? 'Active' : 'Inactive'}
                    </StatusPill>
                  </Td>
                  <Td>
                    <ActionsCell>
                      <SecondaryButton
                        type="button"
                        onClick={() => openEdit(row)}
                        disabled={!!row.deleted_at}
                      >
                        Edit
                      </SecondaryButton>
                      <SecondaryButton
                        type="button"
                        onClick={() => onToggle(row)}
                        disabled={!!row.deleted_at || submitting}
                      >
                        {row.is_active ? 'Deactivate' : 'Activate'}
                      </SecondaryButton>
                      <DangerButton
                        type="button"
                        onClick={() => setDeleteTarget(row)}
                        disabled={!!row.deleted_at}
                      >
                        Delete
                      </DangerButton>
                    </ActionsCell>
                  </Td>
                </Tr>
              ))
            )}
          </tbody>
        </Table>
      </TableWrap>

      {formModal && (
        <Modal
          onClick={closeForm}
          role="presentation"
        >
          <ModalCard onClick={(e) => e.stopPropagation()}>
            <ModalTitle>
              {formMode === 'create' ? 'Add Advisory Category' : 'Edit Advisory Category'}
            </ModalTitle>
            {fieldErrors._form && <FieldError style={{ display: 'block', marginBottom: 12 }}>{fieldErrors._form}</FieldError>}
            <form onSubmit={onSubmitForm}>
              <FormGrid>
                <FormGroup>
                  <Label htmlFor="f-name">Name *</Label>
                  <Input
                    id="f-name"
                    value={formValues.name}
                    onChange={(e) => handleNameChange(e.target.value)}
                    autoComplete="off"
                  />
                  {fieldErrors.name && <FieldError>{fieldErrors.name}</FieldError>}
                </FormGroup>
                <FormGroup>
                  <Label htmlFor="f-slug">Slug *</Label>
                  <Input
                    id="f-slug"
                    value={formValues.slug}
                    onChange={(e) => setFormValues((v) => ({ ...v, slug: e.target.value }))}
                    autoComplete="off"
                  />
                  {fieldErrors.slug && <FieldError>{fieldErrors.slug}</FieldError>}
                </FormGroup>
                <FormGroup>
                  <Label htmlFor="f-desc">Description</Label>
                  <TextArea
                    id="f-desc"
                    value={formValues.description}
                    onChange={(e) => setFormValues((v) => ({ ...v, description: e.target.value }))}
                  />
                  {fieldErrors.description && <FieldError>{fieldErrors.description}</FieldError>}
                </FormGroup>
                <FormGroup>
                  <Label htmlFor="f-order">Display Order *</Label>
                  <Input
                    id="f-order"
                    type="number"
                    min="0"
                    value={formValues.display_order}
                    onChange={(e) => setFormValues((v) => ({ ...v, display_order: e.target.value }))}
                  />
                  {fieldErrors.display_order && <FieldError>{fieldErrors.display_order}</FieldError>}
                </FormGroup>
              </FormGrid>
              <ButtonRow>
                <GhostButton type="button" onClick={closeForm}>Cancel</GhostButton>
                <PrimaryModalButton type="submit" disabled={submitting}>
                  {submitting ? 'Saving...' : formMode === 'create' ? 'Create Category' : 'Update Category'}
                </PrimaryModalButton>
              </ButtonRow>
            </form>
          </ModalCard>
        </Modal>
      )}

      {deleteTarget && (
        <Modal
          onClick={() => setDeleteTarget(null)}
          role="presentation"
        >
          <ModalCard onClick={(e) => e.stopPropagation()}>
            <ModalTitle>Confirm Delete</ModalTitle>
            <p style={{ color: '#4a5568', marginBottom: '1.5rem' }}>
              Are you sure you want to delete the category "{deleteTarget.name}"? This action can be undone.
            </p>
            <ButtonRow>
              <GhostButton type="button" onClick={() => setDeleteTarget(null)}>Cancel</GhostButton>
              <DangerButton type="button" onClick={confirmDelete} disabled={submitting}>
                {submitting ? 'Deleting...' : 'Delete Category'}
              </DangerButton>
            </ButtonRow>
          </ModalCard>
        </Modal>
      )}
    </Container>
  );
}

export default AdvisoryCategories;
